import React, { memo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";

import { CheckIcon, ChevronDown, ChevronUp } from "../../Icons";

interface SelectMenuItem {
  checked: boolean;
  imageSrc: string;
  title: string;
  value: string;
}

function SelectMenuItem ({
  value,
  checked,
  imageSrc,
  title,
  ...rest
}: SelectMenuItem) {
  return (
    <MenuItem value={value} disableRipple {...rest}>
      <Checkbox
        checked={checked}
        icon={<></>}
        checkedIcon={<CheckIcon fontSize="inherit" />}
        sx={{
          width: "24px",
          height: "24px",
          borderRadius: "6px",
          border: "1px solid #667085",
          fontSize: "16px",
          marginRight: "16px",

          "&:hover": {
            borderColor: "#BF295C",
            boxShadow: "#FFF2F7 0px 0px 0px 4px",
          },

          "&.Mui-checked": {
            backgroundColor: "#FFF8FA",
            borderColor: "#BF295C",
            color: "#F56193",
          },

          "& .MuiSvgIcon-root": {
            position: "relative",
            top: "-1px",
          },
        }}
        disableRipple
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Avatar
          src={imageSrc}
          sx={{
            width: "24px",
            height: "24px",
            marginRight: "8px",
          }}
        />
        <Typography>{title}</Typography>
      </Box>
    </MenuItem>
  );
}

interface DefaultArraySelectorProps {
  valuesArray: Array<any>;
  valuesToSelect: Array<any>;
  onChanged: (newArrayValue: Array<any>) => void;
  title: string;
  sx?: any;
  placeholder?: string;
}

const DefaultArraySelector = memo(({
  valuesArray,
  valuesToSelect,
  onChanged,
  title,
  sx,
  placeholder,
}: DefaultArraySelectorProps) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const handleChange = ({ target: { value } }: any) => {
    onChanged(value);
  };

  const renderValue = (value: any) => {
    const arrayMatches =
      valuesToSelect?.filter(({ id }: any) =>
        value.find((val: any) => val === id)
      ) || [];

    return value.length > 0 ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
        }}
      >
        {arrayMatches.map((currentMatch: any) =>
          arrayMatches.length > 1 ? (
            <Typography
              sx={{
                color: "#1F1F1F",
                fontSize: "16px",
                margin: "0 5px 0 0",
              }}
            >
              {` ${currentMatch.name};`}
            </Typography>
          ) : (
            <Typography
              sx={{
                color: "#1F1F1F",
                fontSize: "16px",
              }}
            >
              {` ${currentMatch.name}`}
            </Typography>
          )
        )}
      </Box>
    ) : (
      <Typography
        sx={{
          color: "#676767",
          fontSize: "16px",
          fontWeight: "400",
        }}
      >
        {`Choose ${  placeholder}`}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          padding: "16px",
          minWidth: 192,
          backgroundColor: "#F3F3F3",
          color: "#1F1F1F",
          border: "1px solid #D6D6DB",
          borderRight: "none",
          borderTopLeftRadius: sx?.borderTopLeftRadius,
          borderBottomLeftRadius: sx?.borderBottomLeftRadius,
        }}
      >
        <Typography
          sx={{
            fontWeight: "500",
            textTransform: "capitalize",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          border: "1px solid #D6D6DB",
          borderLeft: "none",
          borderTopRightRadius: sx?.borderTopRightRadius,
          borderBottomRightRadius: sx?.borderBottomRightRadius,
          ...(isSelectOpen && {
            border: "none",
            borderRadius: 0,
          }),
        }}
      >
        <FormControl sx={{ width: "100%", maxWidth: "535px" }}>
          <Select
            placeholder="Select input"
            multiple
            value={valuesArray}
            onChange={handleChange}
            input={<OutlinedInput />}
            IconComponent={() => (
              <>
                {isSelectOpen ? (
                  <ChevronUp fontSize="inherit" />
                ) : (
                  <ChevronDown fontSize="inherit" />
                )}
              </>
            )}
            renderValue={renderValue}
            displayEmpty
            sx={{
              borderRadius: "0px",
              color: "#828282",
              fontWeight: "400",
              height: "56px",

              "& .MuiSvgIcon-root": {
                fontSize: "24px",
                marginRight: "16px",
                color: "#828282",
              },
              boxShadow: "none",

              ...(isSelectOpen && {
                border: "none",
                borderRadius: 0,
                borderTopRightRadius: "2px",
                borderTopLeftRadius: "2px",
                boxShadow: "rgb(0, 0, 0, 0.1) 0px 0px 3px 0px",
              }),

              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 0,
                  boxShadow: "rgb(0, 0, 0, 0.1) 0px 3px 3px 0px",

                  "& .MuiList-root": {
                    padding: 0,
                  },

                  "& .MuiMenuItem-root": {
                    minWidth: 120,
                    color: "#1F1F1F",
                    fontSize: "14px",
                    height: "56px",
                    margin: "0",
                    padding: "16px",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#FFF8FA",

                      "& .MuiCheckbox-root": {
                        backgroundColor: "white",
                        borderColor: "#BF295C",
                        boxShadow: "#FFF2F7 0px 0px 0px 4px",
                      },

                      "& .Mui-checked": {
                        backgroundColor: "transparent",
                      },
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: "transparent",
                    },
                  },
                },
              },
            }}
          >
            {valuesToSelect.map(({ name, surname, imageSrc, id }: any) => (
              <SelectMenuItem
                key={id}
                value={id}
                checked={!!~valuesArray?.indexOf(id)}
                imageSrc={imageSrc}
                title={`${name  } ${  surname}`}
              />
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
})

export {DefaultArraySelector}
